import type { ClientMetric } from '@moment/srv/api/client-metric-labels.ts';
import { normalizeClientMetricLabels } from '@moment/srv/api/client-metric-labels.ts';

export interface IFotoscapeApiMetric extends Record<string, string | number> {
  url: string;
}

const BATCH_SIZE = 10;
const FLUSH_INTERVAL_MS = 5000;

export class Metrics {
  private metricsGateway = '/api';
  private commonLabelAndValues: any;
  private enabled: boolean;
  private pendingMetrics: ClientMetric[] = [];
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private lastFlushTime = Date.now();
  private unloadListenerRegistered = false;

  constructor() {
    this.enabled = !(import.meta.env.SSR);
  }

  initialize() {
    if (!this.commonLabelAndValues && this.enabled && typeof window !== 'undefined') {
      this.commonLabelAndValues = {
        content_domain: window?.location?.host,
      };
    }

    if (this.enabled && typeof window !== 'undefined' && !this.unloadListenerRegistered) {
      this.unloadListenerRegistered = true;
      window.addEventListener('pagehide', () => this.flushBatch());
    }
  }

  private async sendBatch(metrics: ClientMetric[]) {
    if (metrics.length === 0) {
      return;
    }

    const payload = JSON.stringify({ metrics });

    try {
      const response = await fetch(`${this.metricsGateway}/m/batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        keepalive: true,
        body: payload,
      });

      if (!response.ok) {
        console.log(`Failed to log metric batch, status: ${response.status}`, { payload });
      }
    } catch (e) {
      console.log(`Failed to log metric batch`, { payload, error: e });
    }
  }

  private scheduleFlush() {
    const elapsed = Date.now() - this.lastFlushTime;
    const delay = Math.max(0, FLUSH_INTERVAL_MS - elapsed);

    if (this.flushTimer !== null) {
      clearTimeout(this.flushTimer);
    }

    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      this.flushBatch();
    }, delay);
  }

  private flushBatch() {
    if (this.flushTimer !== null) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }

    if (this.pendingMetrics.length === 0) {
      return;
    }

    const batch = this.pendingMetrics.splice(0);
    this.lastFlushTime = Date.now();
    void this.sendBatch(batch);
  }

  private enqueueMetric(metric: ClientMetric) {
    const normalizedMetric: ClientMetric =
      metric.type === 'histogram'
        ? {
            ...metric,
            labels: normalizeClientMetricLabels(metric.name, metric.labels),
          }
        : {
            ...metric,
            labels: normalizeClientMetricLabels(metric.name, metric.labels),
          };

    this.pendingMetrics.push(normalizedMetric);

    if (this.pendingMetrics.length >= BATCH_SIZE) {
      this.flushBatch();
      return;
    }

    this.scheduleFlush();
  }

  public histogram(
    metricName: string,
    value: number,
    labelsAndValues: Partial<Record<string, string>>
  ) {
    if (!this.enabled) {
      return;
    }

    this.enqueueMetric({
      type: 'histogram',
      name: `fotoscapes_player_${metricName}`,
      value,
      labels: { ...this.commonLabelAndValues, ...labelsAndValues },
    });
  }

  public counter(metricName: string, labelsAndValues: Record<string, string>) {
    if (!this.enabled) {
      return;
    }

    this.enqueueMetric({
      type: 'counter',
      name: `fotoscapes_player_${metricName}`,
      labels: { ...this.commonLabelAndValues, ...labelsAndValues },
    });
  }
}

// Create a singleton instance of the Metrics class
const metricsInstance = new Metrics();
metricsInstance.initialize();

// Export the instance
export default metricsInstance;
